import React from 'react';
import styled from 'styled-components';
import Transition from 'components/Transition';
import { Backdrop } from '../Modal/modalStyle';

/* ── Brand tokens ────────────────────────────────────────────────────
   Sourced from integralthemes/theme/tokens.css
   Primary:  #600b68  (plum)
   Magenta:  #b8357b
   Teal:     #4dadb3
   Yellow:   #ffbc50
   Accent bg:#efdbf1
   Text:     #000000 / #374151 / #6b6b6b
   Font:     'Helvetica Neue', Helvetica, Arial, sans-serif
──────────────────────────────────────────────────────────────────── */

const FONT = "'Helvetica Neue', Helvetica, Arial, sans-serif";

/* ── Modal shell ─────────────────────────────────────────────────── */

const Sheet = styled.div`
  position: relative;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.35);
  width: 94vw;
  max-width: 860px;
  max-height: 92vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  font-family: ${FONT};
`;

const CloseBtn = styled.button`
  position: sticky;
  top: 0;
  align-self: flex-end;
  margin: 1rem 1rem 0;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #d0d0d0;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 1.8rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  flex-shrink: 0;
  z-index: 10;
  font-family: ${FONT};

  &:hover { background: #efdbf1; }
`;

/* ── Hero section ────────────────────────────────────────────────── */

const Hero = styled.section`
  background: #600b68;
  padding: 2.5rem 2.5rem 2rem;
`;

const HeroInner = styled.div`
  max-width: 680px;
  & > * + * { margin-top: 1.25rem; }
`;

const Eyebrow = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: #efdbf1;
  margin: 0;
  font-family: ${FONT};
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #fff;
  line-height: 1.2;
  margin: 0;
  font-family: ${FONT};
`;

const HeroBody = styled.p`
  font-size: 1.05rem;
  line-height: 1.75;
  color: #efdbf1;
  margin: 0;
  font-family: ${FONT};
`;

/* ── Content grid ────────────────────────────────────────────────── */

const ContentGrid = styled.section`
  display: grid;
  grid-template-columns: 1.25fr 0.75fr;
  gap: 2rem;
  padding: 2rem 2.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MainCol = styled.div`
  & > * + * { margin-top: 1.5rem; }
`;

const Aside = styled.aside`
  & > * + * { margin-top: 1.25rem; }
`;

/* ── Cards ───────────────────────────────────────────────────────── */

const Card = styled.div`
  border-radius: 20px;
  border: 1px solid #ebebeb;
  background: #fff;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(96, 11, 104, 0.07);
`;

const CardLight = styled(Card)`
  background: #efdbf1;
  border-color: rgba(96, 11, 104, 0.15);
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000;
  margin: 0 0 0.75rem;
  font-family: ${FONT};
`;

const CardBody = styled.div`
  font-size: 1rem;
  line-height: 1.75;
  color: #374151;
  font-family: ${FONT};

  & > p { margin: 0; }
  & > p + p { margin-top: 0.75rem; }
`;

/* ── Tips 2×2 grid ───────────────────────────────────────────────── */

const TipsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TipCard = styled.div`
  border-radius: 14px;
  background: #fff;
  padding: 1.1rem;
  box-shadow: 0 1px 3px rgba(96, 11, 104, 0.09);
`;

const TipIcon = styled.img`
  width: 36px;
  height: 36px;
  margin-bottom: 0.6rem;
  display: block;
`;

const TipTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #000;
  margin: 0 0 0.4rem;
  font-family: ${FONT};
`;

const TipBody = styled.p`
  font-size: 0.975rem;
  line-height: 1.6;
  color: #374151;
  margin: 0;
  font-family: ${FONT};
`;

/* ── Aside cards ─────────────────────────────────────────────────── */

const AsideTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #000;
  margin: 0 0 0.75rem;
  font-family: ${FONT};
`;

const GlanceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 1rem;
  line-height: 1.7;
  color: #374151;
  font-family: ${FONT};

  & > li + li { margin-top: 0.4rem; }
`;

const ResourceLink = styled.a`
  display: block;
  border-radius: 12px;
  border: 1px solid #ebebeb;
  padding: 0.875rem;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s, border-color 0.15s;
  font-size: 0.975rem;
  font-family: ${FONT};

  &:hover {
    background: #efdbf1;
    border-color: rgba(96, 11, 104, 0.3);
  }

  & > span:first-child {
    display: block;
    font-weight: 700;
    color: #600b68;
    margin-bottom: 0.15rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
`;

const ResourceLinks = styled.div`
  & > * + * { margin-top: 0.75rem; }
`;

/* ── Video section ───────────────────────────────────────────────── */

const VideoSection = styled.section`
  background: #4a0852;
  color: #fff;
  padding: 2rem 2.5rem 2.5rem;
  border-radius: 0 0 12px 12px;
`;

const VideoTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 0.6rem;
  font-family: ${FONT};
`;

const VideoDesc = styled.p`
  font-size: 1rem;
  line-height: 1.75;
  color: #efdbf1;
  margin: 0;
  font-family: ${FONT};
`;

const VideoWrapper = styled.div`
  margin-top: 1.5rem;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: #000;
  aspect-ratio: 16 / 9;

  & iframe {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

/* ─────────────────────────────────────────────────────────────────── */

interface Props {
  onClose: () => void;
}

export default function AboutModal({ onClose }: Props) {
  return (
    <Backdrop onClick={onClose}>
      <Transition className="fade" enter timeout={400}>
        <Sheet onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <CloseBtn onClick={onClose} aria-label="Close">×</CloseBtn>

          {/* ── Hero ─────────────────────────────────────────── */}
          <Hero>
            <HeroInner>
              <Eyebrow>About This Interview</Eyebrow>
              <HeroTitle>A simple framework for noticing what fits</HeroTitle>
              <HeroBody>
                This experience is inspired by the idea of <em>ikigai</em>, often described as the
                intersection of what you love, what you are good at, what the world needs, and what
                you can be paid to do. We use that framework as a starting point for reflection, not
                as a test or a formula.
              </HeroBody>
              <HeroBody>
                For this version, we made one important change for younger users. Instead of asking
                about what you can be paid to do, we ask what you are recognized for. That makes the
                conversation more useful for students and emerging professionals who are still
                exploring school, activities, service, and early work experiences.
              </HeroBody>
            </HeroInner>
          </Hero>

          {/* ── Content ──────────────────────────────────────── */}
          <ContentGrid>
            <MainCol>
              <Card>
                <CardTitle>Why we use this approach</CardTitle>
                <CardBody>
                  <p>
                    Career exploration can feel abstract, especially when you are early in the
                    process. This interview is designed to make it more concrete by asking about
                    examples from your life: what pulls you in, what others notice about you, what
                    you do well, and what kinds of contribution feel meaningful.
                  </p>
                  <p>
                    The goal is not to identify one perfect answer. The goal is to notice patterns.
                    Those patterns can help you name strengths, imagine possible paths, and generate
                    a diagram you can revise as you learn more about yourself.
                  </p>
                </CardBody>
              </Card>

              <CardLight>
                <CardTitle>How to use the interview well</CardTitle>
                <TipsGrid>
                  <TipCard>
                    <TipIcon src={`${process.env.PUBLIC_URL}/media/images/be-specific.svg`} alt="" />
                    <TipTitle>Be specific</TipTitle>
                    <TipBody>
                      Real examples from school, hobbies, family responsibilities, volunteer work,
                      teams, and projects lead to better suggestions.
                    </TipBody>
                  </TipCard>
                  <TipCard>
                    <TipIcon src={`${process.env.PUBLIC_URL}/media/images/think-patterns.svg`} alt="" />
                    <TipTitle>Think in patterns</TipTitle>
                    <TipBody>
                      You do not need a polished answer. The interview works best when you notice
                      what keeps showing up across different parts of your life.
                    </TipBody>
                  </TipCard>
                  <TipCard>
                    <TipIcon src={`${process.env.PUBLIC_URL}/media/images/use-voice.svg`} alt="" />
                    <TipTitle>Use voice if helpful</TipTitle>
                    <TipBody>
                      Some people think more clearly out loud. Voice mode is optional, but it can
                      help you respond more naturally and with more detail.
                    </TipBody>
                  </TipCard>
                  <TipCard>
                    <TipIcon src={`${process.env.PUBLIC_URL}/media/images/revise-grow.svg`} alt="" />
                    <TipTitle>Revise as you grow</TipTitle>
                    <TipBody>
                      Your diagram is a snapshot, not a verdict. You can revisit the interview later
                      as your interests and experiences evolve.
                    </TipBody>
                  </TipCard>
                </TipsGrid>
              </CardLight>
            </MainCol>

            <Aside>
              <Card>
                <AsideTitle>At a glance</AsideTitle>
                <GlanceList>
                  <li>• Inspired by the ikigai framework</li>
                  <li>• Adapted for younger users</li>
                  <li>• Focused on reflection, not testing</li>
                  <li>• Designed to create a revisable visual diagram</li>
                </GlanceList>
              </Card>

              <Card>
                <AsideTitle>Explore more</AsideTitle>
                <ResourceLinks>
                  <ResourceLink
                    href="https://medium.com/ideas-into-action/ikigai-the-perfect-career-diagnostic-3de932834be7"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Article</span>
                    <span>Ikigai Explained</span>
                  </ResourceLink>
                  <ResourceLink
                    href="https://positivepsychology.com/ikigai/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Overview</span>
                    <span>Philosophy and graphic</span>
                  </ResourceLink>
                  <ResourceLink
                    href="https://www.youtube.com/watch?v=G2SqqjRn_c0"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>Video</span>
                    <span>Counseling from ikigai</span>
                  </ResourceLink>
                </ResourceLinks>
              </Card>
            </Aside>
          </ContentGrid>

          {/* ── Video ────────────────────────────────────────── */}
          <VideoSection>
            <VideoTitle>Watch a short explainer</VideoTitle>
            <VideoDesc>
              This video offers one accessible entry point into the broader conversation around
              ikigai and how people use it to think about direction and purpose.
            </VideoDesc>
            <VideoWrapper>
              <iframe
                src="https://www.youtube.com/embed/G2SqqjRn_c0?si=5J7XecjHS3PLFQph"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </VideoWrapper>
          </VideoSection>
        </Sheet>
      </Transition>
    </Backdrop>
  );
}
